import React from "react";
import MarketingLayout, { SpaLink } from "./MarketingLayout";

const PROVIDER = {
  "@type": "Organization",
  name: "GearCheckAuto",
  url: "https://gearcheckauto.com/",
};

// What the inspection is commonly called, by state/region. This focuses on the NAMES
// (which are stable) - actual requirements vary by state, county, vehicle age, and fuel
// type and change over time, so the copy points readers to their state DMV/DEQ.
const REGIONAL_NAMES = [
  { term: "Smog check / smog test", where: "California, Nevada, and parts of Arizona" },
  { term: "Emissions test / emissions inspection", where: "Georgia, Colorado, Illinois, Missouri, Tennessee, and others (often by county)" },
  { term: "E-Check", where: "Ohio" },
  { term: "Air Care", where: "Colorado (Air Care Colorado)" },
  { term: "VEIP - Vehicle Emissions Inspection Program", where: "Maryland" },
  { term: "State inspection / annual inspection", where: "Texas, New York, Pennsylvania, Virginia, Maine, Missouri" },
  { term: "Safety inspection", where: "Pennsylvania, New York, Virginia, and many others" },
  { term: "Inspection sticker / windshield sticker / state sticker / state tags", where: "Pennsylvania, New York, Texas, Virginia, Massachusetts, Maine" },
  { term: "VIP - Vehicle Inspection Program", where: "Maine" },
  { term: "I/M (Inspection and Maintenance) program", where: "the federal/EPA term for state emissions programs" },
  { term: "OBD-II test / plug-in test", where: "the standard method for 1996-and-newer vehicles" },
  { term: "Tailpipe / dyno test", where: "used for many older vehicles" },
  { term: "Registration renewal inspection", where: "states that tie the check to your tag/registration renewal" },
];

const FAQ = [
  {
    q: "What is the difference between an emissions test and a safety inspection?",
    a: "An emissions test (also called a smog check) measures the pollutants your vehicle produces and confirms its emissions systems work. A safety inspection checks that key components - brakes, lights, tires, steering, and more - are in safe working order. Some states require one, some require the other, and many combine both into a single 'state inspection.'",
  },
  {
    q: "Does my car need an emissions test or inspection?",
    a: "It depends on your state, and often your county, plus the vehicle's age and fuel type - many states exempt new, very old, electric, or diesel vehicles. Rules change over time, so check your state DMV, DEQ, or emissions program for the current requirement where you live.",
  },
  {
    q: "How much does a vehicle inspection or emissions test cost?",
    a: "It varies by state and station, but emissions and safety inspections are usually a modest flat fee. The bigger cost is any repair needed to pass. With GearCheckAuto you can send one request to several nearby shops and compare repair quotes side by side.",
  },
  {
    q: "My check engine light is on - will I fail emissions?",
    a: "Usually, yes. On OBD-II vehicles an illuminated check engine light is typically an automatic emissions failure because it signals an active fault. Have a shop diagnose and fix the underlying issue, clear the code, then re-test.",
  },
  {
    q: "Can a mobile mechanic help me pass?",
    a: "Official testing is usually done at a licensed station, but a shop or mobile mechanic can diagnose why you failed, make the repairs needed to pass, and get you ready to re-test. Find one fast on GearCheckAuto.",
  },
];

const JSON_LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Vehicle inspection and emissions testing",
    name: "Vehicle inspection and emissions testing help",
    provider: PROVIDER,
    areaServed: { "@type": "Country", name: "United States" },
    url: "https://gearcheckauto.com/vehicle-inspection",
    description:
      "Understand state vehicle inspections and emissions testing (smog check, E-Check, state inspection, inspection sticker, and more), and find a nearby shop or mobile mechanic to fix what is keeping you from passing.",
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://gearcheckauto.com/" },
      {
        "@type": "ListItem",
        position: 2,
        name: "Vehicle inspection",
        item: "https://gearcheckauto.com/vehicle-inspection",
      },
    ],
  },
];

export default function VehicleInspectionPage({ onNavigate }) {
  return (
    <MarketingLayout onNavigate={onNavigate} jsonLd={JSON_LD}>
      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 dark:from-blue-950/40 to-white px-4 py-16">
        <div className="mx-auto max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">Vehicle inspections</p>
          <h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-gray-100 md:text-5xl">
            State vehicle inspections: emissions, safety, and stickers
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600 dark:text-gray-300">
            Depending on where you live, getting your car "inspected" can mean a smog check, an
            emissions test, a safety inspection, an E-Check, or the annual state inspection that earns
            your windshield sticker. Whatever it is called near you, the goal is the same: prove your
            vehicle is safe and clean enough to stay on the road. If something is keeping you from
            passing, GearCheckAuto helps you find a nearby shop or mobile mechanic fast - send one
            request, compare quotes and response times, and book the best fit.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-500"
            >
              Find a shop to help you pass
            </button>
            <button
              type="button"
              onClick={() => onNavigate("autoRepair")}
              className="rounded-lg border border-blue-600 px-7 py-3 text-base font-semibold text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40"
            >
              Need a repair?
            </button>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-3xl px-4 py-12 text-slate-700 dark:text-gray-200">
        <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">What is a vehicle inspection?</h2>
        <p className="mt-4 leading-relaxed">
          "Vehicle inspection" is an umbrella term for a few different checks states use to keep unsafe
          or high-polluting cars off the road. There are really three overlapping things:
        </p>
        <ul className="mt-4 list-disc space-y-2 pl-6">
          <li><span className="font-semibold text-slate-900 dark:text-gray-100">Emissions test (smog check)</span> - measures tailpipe and evaporative emissions and confirms your emissions systems work.</li>
          <li><span className="font-semibold text-slate-900 dark:text-gray-100">Safety inspection</span> - confirms brakes, lights, tires, steering, and other components are in safe condition.</li>
          <li><span className="font-semibold text-slate-900 dark:text-gray-100">Inspection sticker / registration</span> - the windshield sticker or registration approval you get once you pass.</li>
        </ul>
        <p className="mt-4 leading-relaxed">
          Many states bundle these into a single "state inspection," while others require only an
          emissions test or only a safety check. That is why the same thing goes by so many different
          names from state to state.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Emissions testing (smog check)</h2>
        <p className="mt-4 leading-relaxed">
          Emissions testing exists because of the federal Clean Air Act and state Inspection and
          Maintenance (I/M) programs. On most 1996-and-newer vehicles, a technician plugs into your
          car's OBD-II port to read its onboard diagnostics; older vehicles may get a tailpipe or dyno
          test. Testers also check the gas cap and evaporative (EVAP) system and do a visual check of
          emissions equipment. A key gotcha: if your <span className="font-semibold text-slate-900 dark:text-gray-100">check engine light is on</span>, you will
          usually fail automatically, because it signals an active fault the test cannot clear.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Safety inspection</h2>
        <p className="mt-4 leading-relaxed">
          A safety inspection confirms the basics that keep you and others safe on the road, typically
          including brakes, headlights and taillights, turn signals, tires and tread depth, steering
          and suspension, windshield and wipers, mirrors, seat belts, and the horn. States that require
          it usually issue a dated sticker showing when the next inspection is due.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">What it is called in your state</h2>
        <p className="mt-4 leading-relaxed">
          Same idea, many names. Here is what people commonly call it around the country - if you have
          heard any of these, you are in the right place:
        </p>
        <ul className="mt-4 space-y-2">
          {REGIONAL_NAMES.map((item) => (
            <li key={item.term} className="leading-relaxed">
              <span className="font-semibold text-slate-900 dark:text-gray-100">{item.term}</span>
              <span className="text-slate-600 dark:text-gray-300"> - {item.where}</span>
            </li>
          ))}
        </ul>
        <p className="mt-4 leading-relaxed">
          Requirements vary by state, county, vehicle age, and fuel type, and they change over time.
          Always confirm what applies to you with your state DMV, DEQ, or emissions program.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Failed your inspection? Here is what to do</h2>
        <p className="mt-4 leading-relaxed">
          Failing is common and usually fixable. A shop can diagnose the cause - a stored fault code, a
          worn brake or tire, a bad oxygen sensor or catalytic converter, an EVAP leak, or a check
          engine light - repair it, and get you ready to re-test. GearCheckAuto is built for exactly
          this: describe the problem once, send a single request to several nearby shops and mobile
          mechanics, and compare real quotes and response times instead of calling around one shop at a
          time.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">
          How to find help with GearCheckAuto
        </h2>
        <ol className="mt-4 list-decimal space-y-3 pl-6">
          <li>Tell us your vehicle and what failed (or the warning light you are seeing) - it takes a couple of minutes.</li>
          <li>Send one request to several nearby shops and mobile mechanics at once.</li>
          <li>Compare quotes and response times, book the repair, then re-test with confidence.</li>
        </ol>
        <p className="mt-4 leading-relaxed">
          Buying a used car and want to know it will pass before you commit? See our{" "}
          <SpaLink view="prePurchaseInspection" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">pre-purchase inspection guide</SpaLink>, or read more about{" "}
          <SpaLink view="autoRepair" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">finding auto repair near you</SpaLink>.
        </p>

        <h2 className="mt-10 text-2xl font-bold tracking-tight text-slate-900 dark:text-gray-100">Common questions</h2>
        <div className="mt-4 space-y-4">
          {FAQ.map((item) => (
            <div key={item.q}>
              <p className="font-semibold text-slate-900 dark:text-gray-100">{item.q}</p>
              <p className="mt-1 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 leading-relaxed">
          More answers are on our <SpaLink view="faq" onNavigate={onNavigate} className="font-semibold text-blue-700 dark:text-blue-300 hover:text-blue-600">FAQ page</SpaLink>.
        </p>
      </article>

      {/* CTA */}
      <section className="bg-slate-900 px-4 py-16">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white">Get back on the road</h2>
          <p className="mt-3 text-slate-300">
            Send one request and let nearby shops and mobile mechanics help you fix it and pass.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => onNavigate("signup")}
              className="rounded-lg bg-red-600 px-7 py-3 text-base font-semibold text-white hover:bg-red-500"
            >
              Get started
            </button>
            <button
              type="button"
              onClick={() => onNavigate("contact")}
              className="rounded-lg border border-slate-500 px-7 py-3 text-base font-semibold text-white hover:bg-slate-800"
            >
              Contact us
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
